import type { OptionPath } from "./optionPath";

/**
 * One runtime option. Its label and hint come from
 * `admin.flowConfigs.options.<id>.label|hint`; a choice's label from
 * `admin.flowConfigs.options.choices.<label>`.
 */
export type OptionSpec =
  | { id: string; path: OptionPath; kind: "boolean" }
  | { id: string; path: OptionPath; kind: "choice"; choices: ReadonlyArray<readonly [value: string, label: string]> }
  | { id: string; path: OptionPath; kind: "number"; min?: number; max?: number; step?: number; decimalScale?: number }
  | { id: string; path: OptionPath; kind: "text" }
  | { id: string; path: OptionPath; kind: "tags" };

/** A titled group of options (`admin.flowConfigs.optionSections.<id>`). Advanced ones start collapsed. */
export interface OptionSectionSpec {
  id: string;
  advanced?: boolean;
  fields: OptionSpec[];
}

const ratio = { kind: "number", min: 0, max: 1, step: 0.05, decimalScale: 2 } as const;

/**
 * `config_options` beyond the sampling parameters, as the extraction contract
 * scopes them: generation options on every LLM step, input options on
 * DATA_EXTRACTION only (the API rejects them on PAGE_DETECTION).
 */
export const llmConfigSections = (stepType: string): OptionSectionSpec[] => [
  {
    id: "generation",
    fields: [
      { id: "mediaResolution", path: ["mediaResolution"], kind: "choice",
        choices: [["low", "low"], ["medium", "medium"], ["high", "high"]] },
      { id: "compactResponseSchema", path: ["compactResponseSchema"], kind: "boolean" },
    ],
  },
  ...(stepType === "PAGE_DETECTION" ? [] : [{
    id: "input",
    fields: [
      { id: "inputMode", path: ["inputMode"], kind: "choice",
        choices: [["document", "inputDocument"], ["ocr", "inputOcr"], ["document_with_ocr", "inputDocumentWithOcr"],
                  ["document_with_tables", "inputDocumentWithTables"]] },
      { id: "missingOcrPolicy", path: ["missingOcrPolicy"], kind: "choice",
        choices: [["fallback", "fallbackToFile"], ["fail", "failExtraction"]] },
      { id: "ocrMinConfidence", path: ["ocrMinConfidence"], ...ratio },
      { id: "ocrBboxMode", path: ["ocrBboxMode"], kind: "choice",
        choices: [["full", "full"], ["coarse", "coarse"], ["none", "none"]] },
    ],
  } satisfies OptionSectionSpec]),
];

const policyChoices = [["skip", "skip"], ["run", "runFullDocument"], ["fail", "failStage"]] as const;

/**
 * Step-level runtime options of an LLM step: chunking on PAGE_DETECTION only,
 * the missing-domain policy on DATA_EXTRACTION only.
 */
export const stepRuntimeSections = (stepType: string): OptionSectionSpec[] => [
  {
    id: "execution",
    fields: [{ id: "fallbackModel", path: ["fallbackModel"], kind: "text" }],
  },
  ...(stepType === "PAGE_DETECTION" ? [{
    id: "chunking",
    advanced: true,
    fields: [
      { id: "minChunkSize", path: ["chunkingOptions", "min_chunk_size"], kind: "number", min: 1 },
      { id: "chunkOverlap", path: ["chunkingOptions", "chunk_overlap"], kind: "number", min: 0 },
      { id: "defaultMaxPagesPerDomain", path: ["chunkingOptions", "default_max_pages_per_domain"], kind: "number", min: 0 },
      { id: "spanClosureBack", path: ["chunkingOptions", "span_closure", "back"], kind: "number", min: 0 },
      { id: "spanClosureDomains", path: ["chunkingOptions", "span_closure", "domains"], kind: "tags" },
      { id: "promptCache", path: ["chunkingOptions", "prompt_cache"], kind: "boolean" },
    ],
  } satisfies OptionSectionSpec] : [{
    id: "missingDomain",
    fields: [
      { id: "unreportedDomainPolicy", path: ["pageDetectionPolicy", "unreported_domain_policy"], kind: "choice",
        choices: policyChoices },
      { id: "emptyDomainPolicy", path: ["pageDetectionPolicy", "empty_domain_policy"], kind: "choice",
        choices: policyChoices },
    ],
  } satisfies OptionSectionSpec]),
];

/** Per-agent override of the step's fallback model. */
export const AGENT_RUNTIME_SECTIONS: OptionSectionSpec[] = [
  {
    id: "agentExecution",
    fields: [{ id: "agentFallbackModel", path: ["fallbackModel"], kind: "text" }],
  },
];

/** Flow-wide document preprocessing (the thumbnail DPI is fixed at 200). */
export const PREPROCESSING_SECTIONS: OptionSectionSpec[] = [
  {
    id: "preprocessing",
    fields: [{ id: "stripPdfLinks", path: ["strip_pdf_links"], kind: "boolean" }],
  },
];
